import { useEffect, type ReactNode } from 'react';
import { SparkleIcon } from '@phosphor-icons/react';
import { T, useGT } from 'gt-react';
import type { WorkbookDefinition, WorkbookOutput } from '@erp/shared-types';
import type { ExecutionRoot } from '@erp/workbooks';
import type { WorkbookEdits } from './workbookEdits';
import { Button } from '@/components/ui/button';
import { AddInstructionButton } from './WorkbookProcessContext';
import {
  WorkbookProcessContext,
  useWorkbookProcessState,
} from './useWorkbookProcess';
import { WorkbookInstructions } from './WorkbookInstructions';
import { WorkbookProcessCell } from './WorkbookProcessCell';
import { WorkbookOutputCell } from './WorkbookOutputCell';
import { WorkbookDataSection } from './WorkbookDataSection';
import { WorkbookRegenerateSopButton } from './WorkbookSopUpdateDialog';
import type { WorkbookDataContext } from './workbookDataItems';
import { schemaGroupReading } from './workbookSchemaGroup';
import { takeWorkbookGenerationRequest } from './workbookGenerationRequest';
import {
  WorkbookGenerationStatus,
  WorkbookRegenerateDialog,
} from './WorkbookGeneration';

/** Without catalog names or loaded previews, the Data section shows what the Definition holds. */
const noDataContext: WorkbookDataContext = {
  catalogName: () => undefined,
  schemaGroupReading: (source) => schemaGroupReading(source, {}, '–'),
  preview: () => undefined,
};

export function WorkbookProcess({
  definition,
  openItem,
  canUpdate,
  edits,
  workbookId,
  renderRunMenu,
  renderOutputRunMenu,
  renderOutputResult,
  renderStepActions,
  data,
}: {
  definition: WorkbookDefinition;
  openItem: (id: string) => void;
  canUpdate: boolean;
  edits: WorkbookEdits;
  workbookId: string;
  /** The Run modes for a Worksheet root's cell. */
  renderRunMenu?: (root: ExecutionRoot) => ReactNode;
  /** An Output cell's Run menu: up to the Worksheets it reads. */
  renderOutputRunMenu?: (output: WorkbookOutput) => ReactNode;
  /** An Output cell's state and download. */
  renderOutputResult?: (output: WorkbookOutput) => ReactNode;
  /** What the Data section knows beyond the Definition: catalog names, loaded previews. */
  data?: WorkbookDataContext;
  /** The integrations' actions on an Instruction. */
  renderStepActions?: (stepId: string) => ReactNode;
}) {
  const gt = useGT();
  const state = useWorkbookProcessState({
    definition,
    edits,
    canUpdate,
    workbookId,
    openItem,
    renderRunMenu,
    renderStepActions,
  });
  const { outline, references, generation } = state;
  // A Workbook the New workbook wizard just made starts generating on arrival.
  useEffect(() => {
    if (!canUpdate || !generation.ready || generation.busy) return;
    if (takeWorkbookGenerationRequest(workbookId)) void generation.generate();
  }, [canUpdate, generation, workbookId]);
  return (
    <WorkbookProcessContext.Provider value={state}>
      <section className="flex max-w-5xl flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-base font-medium">
            <T context="workbook process heading">Process</T>
          </h2>
          <div className="flex items-center gap-2">
            {canUpdate && (
              <WorkbookRegenerateSopButton
                stepIds={definition.processSteps.map((step) => step.id)}
                whole
                disabled={generation.busy}
              />
            )}
            {canUpdate && (
              <Button
                size="sm"
                variant="outline"
                disabled={
                  !generation.ready ||
                  generation.busy ||
                  (!definition.instructions.trim() &&
                    !definition.processSteps.some((step) =>
                      step.instructions.trim(),
                    ))
                }
                onClick={() => void generation.generate()}
              >
                <SparkleIcon />
                <T context="workbook generate process">Generate with AI</T>
              </Button>
            )}
          </div>
        </div>
        <WorkbookInstructions
          definition={definition}
          canUpdate={canUpdate}
          edits={edits}
          mentionSources={references.mentionSources}
          collapsed={outline.nodes.length > 0}
        />
        {!generation.requestedStepId && generation.generating && (
          <WorkbookGenerationStatus />
        )}
        {!generation.requestedStepId && generation.error && (
          <p role="alert" className="text-sm text-destructive">
            {generation.error}
          </p>
        )}
        <WorkbookDataSection context={data ?? noDataContext} />
        <ol
          className="flex flex-col gap-5"
          aria-label={gt('Process steps', { context: 'workbook process list' })}
        >
          {outline.cells
            .filter((cell) => cell.head.kind !== 'instruction')
            .map((cell) => (
              <WorkbookProcessCell key={cell.head.id} cell={cell} />
            ))}
          {/* Outputs read the Worksheets' Results, so they come after them. */}
          {definition.outputs.map((output) => (
            <WorkbookOutputCell
              key={output.id}
              output={output}
              runMenu={renderOutputRunMenu?.(output)}
              result={renderOutputResult?.(output)}
            />
          ))}
          {outline.cells
            .filter((cell) => cell.head.kind === 'instruction')
            .map((cell) => (
              <WorkbookProcessCell key={cell.head.id} cell={cell} />
            ))}
        </ol>
        <AddInstructionButton />
        <WorkbookRegenerateDialog />
      </section>
    </WorkbookProcessContext.Provider>
  );
}
